import React from 'react';
import { Link } from 'react-router-dom';
import {
  FaCamera,
  FaFileContract,
  FaShieldAlt,
  FaUsers,
} from 'react-icons/fa';
import './FooterPage.css';

const aboutContent = {
  icon: <FaUsers />,
  title: 'À propos de PhotoMatch',
  eyebrow: 'Notre mission',
  intro:
    "PhotoMatch aide les participants à retrouver leurs photos d'événements et donne aux photographes un espace clair pour organiser, partager et vendre leur travail.",
  sections: [
    {
      title: 'Pour les participants',
      body:
        'Un profil, une photo de référence et une galerie plus facile à explorer. Les souvenirs importants deviennent rapides à retrouver.',
    },
    {
      title: 'Pour les photographes',
      body:
        "Des événements centralisés, une distribution simplifiée et des outils pour suivre les galeries, les achats et l'activité.",
    },
    {
      title: 'Une expérience simple',
      body:
        "L'application reste concentrée sur les tâches essentielles : importer, rechercher, consulter, acheter et livrer les photos.",
    },
  ],
};

const legalContent = {
  terms: {
    icon: <FaFileContract />,
    title: "Conditions d'utilisation",
    eyebrow: 'Cadre du service',
    updatedAt: 'Dernière mise à jour : 1er octobre 2026',
    intro:
      "Veuillez lire ces Conditions avant de créer un compte ou d'utiliser PhotoMatch. En cochant la case d'acceptation, vous confirmez que vous avez lu et accepté ces conditions.",
    sections: [
      {
        title: '1. Objet du service',
        body: [
          "PhotoMatch aide les photographes, organisateurs d'événements et participants à gérer, rechercher, consulter, vendre, acheter et télécharger des photos d'événements.",
          "Le service peut inclure des galeries, des QR codes, des liens d'accès, des profils, des outils de recherche de photos, des paiements, des abonnements, des notifications et des fonctionnalités d'analyse.",
          "PhotoMatch est un outil de découverte et de livraison de photos. Il ne doit pas être utilisé comme système de sécurité, de surveillance, de vérification d'identité ou de décision importante.",
        ],
      },
      {
        title: '2. Création de compte',
        body: [
          "Vous devez fournir des informations exactes et garder vos identifiants confidentiels. Vous êtes responsable de toute activité réalisée avec votre compte.",
          "PhotoMatch peut demander une vérification par email, une authentification à deux facteurs ou tout autre contrôle raisonnable pour protéger votre compte.",
        ],
      },
      {
        title: '3. Âge minimum',
        body: [
          "Vous devez avoir au moins 13 ans pour utiliser PhotoMatch. Si vous n'avez pas l'âge de la majorité dans votre pays ou région, vous devez utiliser le service avec l'autorisation d'un parent ou tuteur légal.",
          "Les photographes et organisateurs sont responsables d'obtenir les autorisations nécessaires lorsqu'ils importent ou traitent des photos de mineurs.",
        ],
      },
      {
        title: '4. Responsabilités des photographes et organisateurs',
        body: [
          "Si vous créez un événement ou importez des photos, vous confirmez disposer des droits, autorisations, licences, consentements et informations nécessaires pour utiliser ces photos sur PhotoMatch.",
          "Vous êtes responsable des informations de l'événement, des prix, des accès, des crédits photographes, du verrouillage de galerie et des conditions communiquées aux participants.",
          "Vous ne devez pas importer de contenu lorsque la publication, la vente, la recherche faciale ou le partage serait illégal, trompeur, dangereux ou contraire aux attentes raisonnables des participants.",
        ],
      },
      {
        title: '5. Responsabilités des participants',
        body: [
          "Si vous importez une photo de référence, vous devez utiliser une image de vous-même ou disposer de l'autorisation claire de la personne concernée.",
          "Vous ne devez pas utiliser PhotoMatch pour rechercher une autre personne sans autorisation, usurper une identité, harceler, surveiller ou porter atteinte à la vie privée de quelqu'un.",
          "Les photos achetées ou téléchargées doivent être utilisées conformément aux droits accordés par le photographe, aux règles de l'événement et à la loi applicable.",
        ],
      },
      {
        title: '6. Recherche faciale et résultats automatisés',
        body: [
          "PhotoMatch peut utiliser la détection ou la comparaison de visages pour aider les utilisateurs à retrouver les photos sur lesquelles ils peuvent apparaître.",
          "Les résultats automatisés peuvent être incomplets ou incorrects. Ils peuvent contenir des faux positifs ou des faux négatifs selon la qualité de l'image, l'angle, la lumière, l'expression, l'âge ou d'autres facteurs.",
          "Vous ne pouvez pas utiliser ces outils pour la surveillance, l'application de la loi, le contrôle d'accès, l'emploi, le logement, le crédit, l'assurance, la santé ou toute autre décision importante.",
        ],
      },
      {
        title: '7. Contenu utilisateur',
        body: [
          "Vous conservez la propriété des photos, images, textes, profils, données d'événements et autres contenus que vous importez.",
          "Vous accordez à PhotoMatch une licence limitée pour héberger, stocker, afficher, traiter, redimensionner, filigraner, analyser et transmettre ce contenu uniquement dans la mesure nécessaire pour fournir, sécuriser, améliorer et maintenir le service.",
          "Vous êtes seul responsable de votre contenu et des réclamations qui pourraient en découler.",
        ],
      },
      {
        title: '8. Achats, paiements et abonnements',
        body: [
          "Les prix, taxes, moyens de paiement, limites, options de livraison et fonctionnalités d'abonnement sont ceux affichés au moment de la commande.",
          "Les paiements peuvent être traités par des prestataires tiers. Une fois un fichier numérique livré ou rendu disponible, l'achat peut être définitif, sauf obligation légale ou indication contraire au moment du paiement.",
          "En cas de paiement échoué, annulé, contesté ou suspect, PhotoMatch peut suspendre l'accès aux téléchargements, aux galeries ou aux avantages concernés pendant l'examen de la situation.",
        ],
      },
      {
        title: '9. Comportements interdits',
        body: [
          "Vous ne devez pas violer la loi, les droits d'auteur, les droits à l'image, la vie privée, les règles d'un événement ou les droits d'un tiers.",
          "Vous ne devez pas contourner les filigranes, contrôles d'accès, paiements, limites techniques, protections de téléchargement ou mesures de sécurité.",
          "Vous ne devez pas extraire en masse des photos, profils, données faciales, données d'événements ou informations d'utilisateurs, ni utiliser PhotoMatch pour entraîner un système concurrent de reconnaissance ou de recherche faciale.",
        ],
      },
      {
        title: '10. Suspension et suppression',
        body: [
          "Nous pouvons suspendre un compte, limiter des fonctionnalités, supprimer du contenu ou désactiver une galerie si nous pensons raisonnablement qu'une règle a été violée, qu'un paiement est problématique ou qu'un risque existe pour les utilisateurs, la sécurité ou le service.",
          "Vous pouvez cesser d'utiliser PhotoMatch à tout moment et demander la suppression de votre compte, sous réserve des obligations légales, transactionnelles, de sécurité et de conservation applicables.",
        ],
      },
      {
        title: '11. Disponibilité et garanties',
        body: [
          "PhotoMatch est fourni tel quel et selon disponibilité. Nous faisons des efforts raisonnables pour maintenir le service, mais nous ne garantissons pas un accès ininterrompu, des résultats de correspondance parfaits ni la conservation permanente de chaque fichier.",
          "Nous pouvons modifier, suspendre ou arrêter certaines fonctionnalités, notamment les formules, limites, prestataires techniques, formats pris en charge ou options de paiement.",
        ],
      },
      {
        title: '12. Responsabilité',
        body: [
          "Dans la limite permise par la loi, PhotoMatch ne sera pas responsable des dommages indirects, pertes de revenus, pertes de données, pertes de contenu, interruptions d'activité ou atteintes à la réputation liés à l'utilisation du service.",
          "Certaines juridictions n'autorisent pas certaines limitations. Dans ce cas, les limitations ci-dessus s'appliqueront uniquement dans la mesure permise par la loi applicable.",
        ],
      },
      {
        title: '13. Confidentialité',
        body: [
          "Notre Politique de confidentialité explique comment nous collectons, utilisons, partageons et conservons les informations personnelles, y compris les informations liées aux photos et à la recherche faciale.",
          "L'utilisation de certaines fonctionnalités peut nécessiter un consentement spécifique, notamment lorsqu'il existe un traitement biométrique ou sensible.",
        ],
      },
      {
        title: '14. Modifications et contact',
        body: [
          "Nous pouvons mettre à jour ces Conditions. En cas de changement important, nous fournirons un avis raisonnable via le service, par email ou par un autre moyen approprié.",
          "Pour toute question, utilisez la page Contact de PhotoMatch ou les coordonnées communiquées dans l'application.",
        ],
      },
    ],
  },
  privacy: {
    icon: <FaShieldAlt />,
    title: 'Politique de confidentialité',
    eyebrow: 'Vos données',
    updatedAt: 'Dernière mise à jour : 1er octobre 2026',
    intro:
      "Veuillez lire cette Politique avant de créer un compte. Elle explique quelles données PhotoMatch traite, pourquoi, avec qui elles peuvent être partagées et quels droits vous pouvez exercer.",
    sections: [
      {
        title: "1. Champ d'application",
        body: [
          "Cette Politique s'applique aux informations traitées lorsque vous utilisez PhotoMatch, notamment le site web, les applications mobiles, les galeries, les outils de recherche de photos, les paiements et les services associés.",
          "Les photographes et organisateurs utilisant PhotoMatch pour leurs événements restent responsables de leurs propres obligations d'information, d'autorisation et de consentement envers les participants.",
        ],
      },
      {
        title: '2. Données de compte',
        body: [
          "Nous pouvons collecter votre nom, adresse email, mot de passe ou identifiants d'authentification, rôle, photo de profil, téléphone, paramètres de compte, préférences, statut de vérification et informations liées à la sécurité.",
          "Ces données servent à créer votre compte, vous authentifier, protéger votre accès, personnaliser votre expérience et fournir le support.",
        ],
      },
      {
        title: "3. Données d'événement et de galerie",
        body: [
          "Nous pouvons traiter les noms d'événements, dates, lieux, catégories, descriptions, visibilités, QR codes, liens d'accès, statuts de verrouillage, crédits photographes, prix, nombres de photos et métadonnées associées.",
          "Ces informations permettent d'organiser les galeries, de gérer les accès, de suivre les ventes et de présenter les événements aux personnes autorisées.",
        ],
      },
      {
        title: '4. Photos, images et références',
        body: [
          "Les utilisateurs peuvent importer des photos d'événements, images de profil, images de référence, miniatures, aperçus filigranés, noms de fichiers, métadonnées, légendes ou descriptions.",
          "Les photos peuvent contenir des personnes identifiables et parfois révéler un contexte sensible, comme un événement privé, professionnel, scolaire, religieux, politique ou médical.",
        ],
      },
      {
        title: '5. Recherche faciale et données sensibles',
        body: [
          "Lorsque vous utilisez la recherche de photos, PhotoMatch peut traiter des images de référence, visages détectés, modèles faciaux, embeddings, scores de similarité, résultats de correspondance et journaux techniques.",
          "Ces données sont utilisées pour aider une personne à retrouver ses propres photos dans une galerie. Elles ne sont pas destinées à la surveillance, à l'application de la loi ou à des décisions importantes.",
          "Lorsque la loi l'exige, un consentement spécifique peut être demandé avant tout traitement biométrique ou sensible.",
        ],
      },
      {
        title: '6. Paiements et achats',
        body: [
          "Nous pouvons traiter des identifiants de transaction, détails de commande, photos achetées, abonnements, reçus, statuts de paiement, remboursements, oppositions et signaux de prévention de la fraude.",
          "Les informations complètes de carte bancaire, mobile money ou compte bancaire sont généralement traitées par des prestataires de paiement tiers et non stockées directement par PhotoMatch.",
        ],
      },
      {
        title: '7. Données techniques',
        body: [
          "Nous pouvons collecter des données comme l'adresse IP, le type d'appareil, le navigateur, le système d'exploitation, la version de l'application, les pages consultées, les actions réalisées, les journaux de sécurité, les erreurs et données de diagnostic.",
          "Nous pouvons utiliser des cookies, le stockage local ou de session et des technologies similaires pour garder la session ouverte, mémoriser des préférences, protéger les comptes, mesurer l'utilisation et améliorer le service.",
        ],
      },
      {
        title: '8. Utilisation des données',
        body: [
          "Nous utilisons les données pour créer et sécuriser les comptes, fournir les galeries, gérer les événements, effectuer les recherches de photos, traiter les paiements, envoyer des reçus et notifications, fournir le support et prévenir les abus.",
          "Nous les utilisons aussi pour maintenir, analyser, améliorer et sécuriser PhotoMatch, respecter la loi, résoudre les litiges et protéger les droits des utilisateurs, photographes, participants et de PhotoMatch.",
        ],
      },
      {
        title: '9. Partage des données',
        body: [
          "Selon les paramètres de l'événement et les rôles, certaines données peuvent être visibles par les photographes, gestionnaires, membres d'équipe ou utilisateurs autorisés à accéder à la galerie.",
          "Nous pouvons partager des données avec des prestataires qui nous aident à fournir l'hébergement, le stockage, le traitement d'image, la recherche faciale, les paiements, l'email, les SMS, l'analyse, la sécurité et le support.",
          "Nous pouvons aussi divulguer des informations si la loi, une procédure légale, une demande réglementaire, une enquête de sécurité ou la protection de droits l'exige.",
        ],
      },
      {
        title: '10. Conservation',
        body: [
          "Nous conservons les données aussi longtemps que nécessaire pour fournir le service, maintenir les comptes, exploiter les galeries, finaliser les transactions, prévenir la fraude, respecter la loi et résoudre les litiges.",
          "Les images de référence, modèles faciaux et données de correspondance sont conservés uniquement aussi longtemps que nécessaire à la fonctionnalité concernée, sauf obligation légale ou besoin de sécurité.",
          "Une suppression des systèmes actifs peut ne pas retirer immédiatement les données des sauvegardes, journaux ou archives nécessaires, mais ces copies sont limitées et supprimées selon nos cycles de conservation.",
        ],
      },
      {
        title: '11. Sécurité',
        body: [
          "Nous utilisons des mesures administratives, techniques et organisationnelles destinées à protéger les informations, comme les contrôles d'accès, l'authentification, le chiffrement en transit, les permissions par rôle, la surveillance et les sauvegardes.",
          "Aucun système n'est parfaitement sécurisé. Vous devez protéger vos identifiants et utiliser des appareils et réseaux fiables.",
        ],
      },
      {
        title: '12. Vos choix et droits',
        body: [
          "Selon votre pays ou région, vous pouvez avoir le droit d'accéder à vos données, les corriger, les supprimer, recevoir une copie, vous opposer à certains traitements, limiter certains usages ou retirer votre consentement.",
          "Le retrait du consentement à la recherche faciale peut limiter ou désactiver certaines fonctionnalités et peut entraîner la suppression des images de référence, modèles, embeddings ou résultats associés.",
          "Pour exercer vos droits, utilisez la page Contact ou les moyens de support proposés par PhotoMatch.",
        ],
      },
      {
        title: '13. Mineurs',
        body: [
          "PhotoMatch n'est pas destiné aux enfants de moins de 13 ans. Les photographes et organisateurs sont responsables d'obtenir les autorisations parentales, scolaires, de lieu ou d'organisation nécessaires avant d'importer ou traiter des photos de mineurs.",
        ],
      },
      {
        title: '14. Transferts internationaux',
        body: [
          "PhotoMatch et ses prestataires peuvent traiter des informations dans des pays différents du vôtre. Lorsque la loi l'exige, nous utilisons des garanties appropriées pour encadrer ces transferts.",
        ],
      },
      {
        title: '15. Modifications et contact',
        body: [
          "Nous pouvons mettre à jour cette Politique de confidentialité. Si les changements sont importants, nous fournirons un avis raisonnable via le service, par email ou par tout autre moyen approprié.",
          "Pour toute question, demande de suppression, retrait de consentement ou réclamation, utilisez la page Contact de PhotoMatch ou les coordonnées communiquées dans l'application.",
        ],
      },
    ],
  },
};

const contentByType = {
  about: aboutContent,
  terms: legalContent.terms,
  privacy: legalContent.privacy,
};

const LegalSection = ({ section }) => (
  <article className="legal-section">
    <h2>{section.title}</h2>
    {section.body.map((paragraph) => (
      <p key={paragraph}>{paragraph}</p>
    ))}
  </article>
);

const FooterPage = ({ type }) => {
  const content = contentByType[type] || aboutContent;
  const isLegal = type === 'terms' || type === 'privacy';

  return (
    <div className="footer-page">
      <section className="footer-page-hero">
        <div className="footer-page-icon">{content.icon || <FaCamera />}</div>
        <p>{content.eyebrow}</p>
        <h1>{content.title}</h1>
        {content.updatedAt && <strong className="footer-page-date">{content.updatedAt}</strong>}
        <span>{content.intro}</span>
      </section>

      {isLegal ? (
        <section className="legal-document" aria-label={content.title}>
          <div className="legal-callout">
            Prenez le temps de lire ce document avant de cocher la case d'acceptation lors de votre inscription.
          </div>
          {content.sections.map((section) => (
            <LegalSection section={section} key={section.title} />
          ))}
        </section>
      ) : (
        <section className="footer-page-content">
          {content.sections.map((section) => (
            <article className="footer-info-card" key={section.title}>
              <h2>{section.title}</h2>
              <p>{section.body}</p>
            </article>
          ))}
        </section>
      )}

      <div className="footer-page-action">
        <Link to="/" className="footer-page-button">
          Retour à l'accueil
        </Link>
      </div>
    </div>
  );
};

export default FooterPage;
